import React, { useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import {
  Search,
  ChevronRight,
  Plus,
  Minus,
  Users,
  ShieldCheck,
  UserCog,
  X,
  Settings2,
} from 'lucide-react';

import { cn } from '../../lib/utils';
import { useForm } from '../../hooks/useForm';
import { useUser } from '../../contexts/UserContext';
import { getUrl } from '../../lib/serviceHelpers';
import { useFetchRoles } from '../../services/roles';
import PrivateLayout from '../layouts/PrivateLayout';
import Pagination from '../common/Pagination';
import UserRolePermissionsPanel from './UserRolePermissionsPanel';
import RoleDefinitionsTab from './RoleDefinitionsTab';

/**
 * User type configuration for tabs
 */
const USER_TYPE_TABS = [
  { key: 'admin', label: 'Internal', icon: ShieldCheck },
  { key: 'client', label: 'Clients', icon: Users },
  { key: 'provider', label: 'Providers', icon: UserCog },
];

/**
 * Fetch users with their roles based on user type
 */
const useFetchUsersWithRoles = (userType, page, search, sortBy, sortDir, filterRoles) => {
  return useQuery(
    ['role-management-users', { userType, page, search, sortBy, sortDir, filterRoles }],
    async () => {
      const url = getUrl('/users', {
        userType,
        page: page || 1,
        search,
        sortBy,
        sortDir,
        includeRoles: true,
        filterRoles: filterRoles || undefined,
      });
      const res = await axios.get(url);
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 5000,
    }
  );
};

/**
 * Role badge color mapping (Tailwind classes)
 */
const ROLE_BADGE_STYLES = {
  admin: 'bg-red-100 text-red-700',
  claims_admin: 'bg-amber-100 text-amber-700',
  billing_admin: 'bg-teal-100 text-teal-700',
  director: 'bg-blue-100 text-blue-700',
  claims_manager: 'bg-emerald-100 text-emerald-700',
  onboarding_specialist: 'bg-slate-200 text-slate-700',
  referral: 'bg-slate-700 text-white',
  provider: 'bg-violet-100 text-violet-700',
  client: 'bg-slate-100 text-slate-600',
  it_support: 'bg-indigo-100 text-indigo-700',
};

const getRoleBadgeStyle = (roleName) =>
  ROLE_BADGE_STYLES[roleName] || 'bg-slate-100 text-slate-600';

/**
 * Format role name for display
 */
const formatRoleName = (roleName) => {
  return roleName
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const RoleManagement = () => {
  const { user: currentUser, hasPermission } = useUser();
  const { form, onChange } = useForm({ search: '' });
  const queryClient = useQueryClient();

  const [topTab, setTopTab] = useState('users');
  const [activeTab, setActiveTab] = useState('admin');
  const [filters, setFilters] = useState({
    page: 1,
    search: undefined,
    sortBy: 'userLastName',
    sortDir: 'ASC',
  });

  // Filter state
  const [filterRoles, setFilterRoles] = useState([]);

  // Panel state (replaces both modals)
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPanel, setShowPanel] = useState(false);

  // Fetch available roles for filter pills
  const { data: rolesData } = useFetchRoles();
  // The role's own display name (renames like Sales Rep live there), falling
  // back to the key when the roles list has not loaded.
  const roleLabel = (role) =>
    (rolesData || []).find((r) => r.roleName === role.roleName)?.roleDisplayName ||
    role.roleDisplayName ||
    formatRoleName(role.roleName);

  const { isLoading, data } = useFetchUsersWithRoles(
    activeTab === 'admin' ? 'internal' : activeTab,
    filters.page,
    filters.search,
    filters.sortBy,
    filters.sortDir,
    filterRoles.length > 0 ? filterRoles.join(',') : undefined
  );

  const onSubmit = (e) => {
    e.preventDefault();
    setFilters({ ...filters, page: 1, search: form.search });
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setFilterRoles([]);
    setFilters({ ...filters, page: 1 });
  };

  const toggleRoleFilter = (roleName) => {
    setFilterRoles((prev) =>
      prev.includes(roleName)
        ? prev.filter((r) => r !== roleName)
        : [...prev, roleName]
    );
    setFilters((prev) => ({ ...prev, page: 1 }));
  };

  const handleUserClick = (row) => {
    setSelectedUser({
      ...row,
      isSelf: row.userId === currentUser?.userId,
    });
    setShowPanel(true);
  };

  const handleClosePanel = () => {
    setShowPanel(false);
  };

  const rows = data?.rows || [];
  const sortBy = filters.sortBy;
  const sortDir = filters.sortDir;

  const handleSortToggle = (key) => {
    let newDir = 'ASC';
    if (sortBy === key && sortDir === 'ASC') {
      newDir = 'DESC';
    }
    setFilters({ ...filters, page: 1, sortBy: key, sortDir: newDir });
  };

  const renderSortIndicator = (key) => {
    if (sortBy !== key) return null;
    return (
      <span className="ml-1 text-slate-400">
        {sortDir === 'ASC' ? '↑' : '↓'}
      </span>
    );
  };

  return (
    <PrivateLayout>
      {/* Top-level tabs */}
      <div className="mb-4 flex justify-center">
        <div className="inline-flex rounded-lg bg-slate-100 p-1">
          <button
            onClick={() => setTopTab('users')}
            className={cn(
              'inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium transition-all rounded-md',
              topTab === 'users' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'
            )}
          >
            <Users className="h-4 w-4" /> User Roles
          </button>
          {hasPermission('roles:edit') && (
            <button
              onClick={() => setTopTab('definitions')}
              className={cn(
                'inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium transition-all rounded-md',
                topTab === 'definitions' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'
              )}
            >
              <Settings2 className="h-4 w-4" /> Role Definitions
            </button>
          )}
        </div>
      </div>

      {/* Page Container */}
      {topTab === 'users' && (
        <div className="mx-auto" style={{ maxWidth: '1200px' }}>
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-semibold text-slate-900 mb-1">
              Role Management
            </h2>
            <p className="text-sm text-slate-500 mb-0">
              Manage user roles and permissions. Click a user to edit their access.
            </p>
          </div>
          <form onSubmit={onSubmit} className="w-full sm:w-72">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              <input
                name="search"
                value={form.search}
                type="search"
                placeholder="Search by name or email..."
                onChange={onChange}
                className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-10 pr-4 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300 transition-colors"
              />
            </div>
          </form>
        </div>

        {/* Card */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          {/* Segmented Tabs */}
          <div className="px-4 pt-4 pb-3">
            <div className="inline-flex rounded-lg bg-slate-100 p-1">
              {USER_TYPE_TABS.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.key}
                    onClick={() => handleTabChange(tab.key)}
                    className={cn(
                      'inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium transition-all rounded-md',
                      activeTab === tab.key
                        ? 'bg-white shadow-sm text-slate-900'
                        : 'text-slate-500 hover:text-slate-700'
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Role Filter Pills — filtered by active tab's group */}
          {rolesData?.length > 0 && (
            <div className="px-4 pb-3 flex items-center gap-2 overflow-x-auto whitespace-nowrap">
              {rolesData.filter((role) => {
                const tabGroup = activeTab === 'admin' ? 'internal' : activeTab;
                return role.roleGroup === tabGroup;
              }).map((role) => {
                const isActive = filterRoles.includes(role.roleName);
                return (
                  <button
                    key={role.roleName}
                    onClick={() => toggleRoleFilter(role.roleName)}
                    className={cn(
                      'inline-flex items-center rounded-full px-3 py-1 text-xs font-medium transition-all border',
                      isActive
                        ? getRoleBadgeStyle(role.roleName) + ' border-transparent'
                        : 'border-slate-200 text-slate-500 bg-white hover:border-slate-300 hover:text-slate-700'
                    )}
                  >
                    {roleLabel(role)}
                  </button>
                );
              })}
              {filterRoles.length > 0 && (
                <button
                  onClick={() => {
                    setFilterRoles([]);
                    setFilters((prev) => ({ ...prev, page: 1 }));
                  }}
                  className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium text-slate-500 hover:text-slate-700 transition-colors"
                >
                  <X className="h-3 w-3" />
                  Clear
                </button>
              )}
            </div>
          )}

          {/* Column Headers */}
          <div className="grid grid-cols-12 gap-4 px-4 py-2 border-b border-slate-100 bg-slate-50/50">
            <div className="col-span-3">
              <button
                onClick={() => handleSortToggle('userLastName')}
                className="text-xs font-medium text-slate-500 uppercase tracking-wider hover:text-slate-700 transition-colors"
              >
                Name{renderSortIndicator('userLastName')}
              </button>
            </div>
            <div className="col-span-3">
              <button
                onClick={() => handleSortToggle('userEmail')}
                className="text-xs font-medium text-slate-500 uppercase tracking-wider hover:text-slate-700 transition-colors"
              >
                Email{renderSortIndicator('userEmail')}
              </button>
            </div>
            <div className="col-span-5">
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                Roles & Permissions
              </span>
            </div>
            <div className="col-span-1" />
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="flex justify-center items-center py-12">
              <Spinner animation="border" variant="primary" />
            </div>
          )}

          {/* Empty State */}
          {!isLoading && rows.length === 0 && (
            <div className="text-center py-12">
              <Users className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No users found</p>
            </div>
          )}

          {/* User Rows */}
          {!isLoading && rows.length > 0 && (
            <div className="divide-y divide-slate-100">
              {rows.map((row) => {
                const roles = row._roles || [];
                const overrides = row._overrideCounts || { granted: 0, revoked: 0 };
                const isSelf = row.userId === currentUser?.userId;

                return (
                  <button
                    key={row.userId}
                    onClick={() => handleUserClick(row)}
                    className="w-full grid grid-cols-12 gap-4 px-4 py-3 text-left hover:bg-slate-50 transition-colors group"
                  >
                    {/* Name */}
                    <div className="col-span-3 flex items-center gap-2 min-w-0">
                      <span className="font-medium text-sm text-slate-900 truncate">
                        {row.userFirstName} {row.userLastName}
                      </span>
                      {isSelf && (
                        <span className="shrink-0 bg-blue-100 text-blue-700 text-xs rounded-full px-2 py-0.5 font-medium">
                          You
                        </span>
                      )}
                    </div>

                    {/* Email */}
                    <div className="col-span-3 flex items-center min-w-0">
                      <span className="text-sm text-slate-500 truncate">
                        {row.userEmail}
                      </span>
                    </div>

                    {/* Roles & Overrides */}
                    <div className="col-span-5 flex items-center gap-1.5 flex-wrap min-w-0">
                      {roles.length === 0 && (
                        <span className="text-sm text-slate-400 italic">No roles</span>
                      )}
                      {roles.map((role) => (
                        <span
                          key={role.roleName}
                          className={cn(
                            'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                            getRoleBadgeStyle(role.roleName)
                          )}
                        >
                          {roleLabel(role)}
                        </span>
                      ))}
                      {overrides.granted > 0 && (
                        <span className="inline-flex items-center gap-0.5 text-xs font-medium text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5">
                          <Plus className="h-3 w-3" />{overrides.granted}
                        </span>
                      )}
                      {overrides.revoked > 0 && (
                        <span className="inline-flex items-center gap-0.5 text-xs font-medium text-red-700 bg-red-100 rounded-full px-2 py-0.5">
                          <Minus className="h-3 w-3" />{overrides.revoked}
                        </span>
                      )}
                    </div>

                    {/* Chevron */}
                    <div className="col-span-1 flex items-center justify-end">
                      <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-slate-500 transition-colors" />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Pagination */}
        {data && data.total > 0 && (
          <div className="mt-4">
            <Pagination
              page={filters.page}
              totalRows={data.total}
              rowsPerPage={50}
              onBack={() =>
                setFilters({ ...filters, page: filters.page - 1 })
              }
              onForward={() =>
                setFilters({ ...filters, page: filters.page + 1 })
              }
            />
          </div>
        )}

        {/* Side Panel */}
        <UserRolePermissionsPanel
          show={showPanel}
          onHide={handleClosePanel}
          user={selectedUser}
          onSuccess={() => queryClient.invalidateQueries(['role-management-users'])}
        />
      </div>
      )}

      {topTab === 'definitions' && <RoleDefinitionsTab />}
    </PrivateLayout>
  );
};

export default RoleManagement;
